import * as Sentry from '@sentry/browser'
import { useCookies } from 'react-cookie'
import { createRoot } from 'react-dom/client'
import { globalAuthContext } from '@headbox-ui/auth-hook'
import * as UrqlModule from 'urql'
import {
  ASSET_SERVICE,
  SENTRY_SERVICE,
  REFRESH_TOKEN_COOKIE_NAME,
  JWT_COOKIE_NAME,
  HEADBOX_ROOT_SERVICE,
  GQL_GATEWAY,
  AUTH_SERVICE,
  SENTRY_ENVIRONMENT,
  PRODUCT_FRUITS_ENABLED,
} from './Constants'
import AppWrapper from './Components/AppWrapper'
import { createFromUrqlClient } from '@headbox-ui/auth-hook/dist/graphQLAdapter'
import { useEffect, useMemo, useState } from 'react'
import AppRouter from './router'
import { BrowserRouter as Router } from 'react-router-dom'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import theme from './Styles/theme'
import Header from './Components/Header/Header'
import ProductFruitsProvider from './Components/ProductFruits/Provider'

__webpack_public_path__ = `${ASSET_SERVICE}/`

const container = document.querySelector('app-root')
if (container === null) throw new Error('No app-root element found')

const root = createRoot(container)

Sentry.init({
  dsn: SENTRY_SERVICE,
  environment: SENTRY_ENVIRONMENT || 'not set',
  release: (window as any).SENTRY_RELEASE?.id || 'local',
})

globalAuthContext.set({
  refreshTokenCookie: REFRESH_TOKEN_COOKIE_NAME,
  sessionTokenCookie: JWT_COOKIE_NAME,
  rootService: HEADBOX_ROOT_SERVICE,
})

const App = (): JSX.Element => {
  const [cookies] = useCookies([REFRESH_TOKEN_COOKIE_NAME, JWT_COOKIE_NAME])
  const [hasSetGlobalAuth, setHasSetGlobalAuth] = useState(false)
  const authOptions = {
    url: AUTH_SERVICE,
    fetchOptions: () => ({
      headers: { 'x-headbox-accesstoken': cookies[JWT_COOKIE_NAME] },
    }),
  }

  const client = useMemo(
    () =>
      UrqlModule.createClient({
        url: GQL_GATEWAY,
        fetchOptions: () => ({
          headers: { 'x-headbox-accesstoken': cookies[JWT_COOKIE_NAME] },
        }),
      }),
    [cookies, cookies[JWT_COOKIE_NAME]]
  )

  const authClient = useMemo(
    () => UrqlModule.createClient(authOptions),
    [cookies, cookies[JWT_COOKIE_NAME]]
  )

  useEffect(() => {
    globalAuthContext.set({
      client: createFromUrqlClient({
        urqlModule: UrqlModule,
        client: authClient,
        ...authOptions,
      }),
      refreshTokenCookie: REFRESH_TOKEN_COOKIE_NAME,
      sessionTokenCookie: JWT_COOKIE_NAME,
      rootService: HEADBOX_ROOT_SERVICE,
    })
    setHasSetGlobalAuth(true)
  }, [authClient])
  if (!hasSetGlobalAuth) return <></>

  return (
    <ProductFruitsProvider enabled={PRODUCT_FRUITS_ENABLED}>
      <Router basename='/event'>
        <UrqlModule.Provider value={client}>
          <ThemeProvider theme={theme}>
            <Header />
            <CssBaseline />
            <AppWrapper>
              <AppRouter />
            </AppWrapper>
          </ThemeProvider>
        </UrqlModule.Provider>
      </Router>
    </ProductFruitsProvider>
  )
}

root.render(<App />)
